/**
 * SingleSelect component — shared types and Zod prop schema.
 *
 * A single-choice selection list for intake flows and questionnaires.
 * Defaults to stacked option cards; inline renders compact separate pill choices.
 * Both layouts retain radio semantics.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import { states } from '../../elements/states';
import { focusRing } from '../../elements/focusRing';
import { sizing } from '../../elements/sizing';
import { IconNameEnum } from '../Icon/Icon';

/**
 * Revealed answer state of an option, for question-and-answer surfaces that
 * show the result after the member commits: `correct` = chosen and right,
 * `incorrect` = chosen and wrong, `missed` = right but not chosen.
 */
export const OptionFeedback = z.enum(['correct', 'incorrect', 'missed']);
export type OptionFeedback = z.infer<typeof OptionFeedback>;

/**
 * A small pill shown above an option's label, e.g. "Recommended". `info` is
 * the neutral highlight; `accent` is the stronger one for an offer. Web only:
 * native ignores it.
 */
export const OptionTagTone = z.enum(['info', 'accent']);
export type OptionTagTone = z.infer<typeof OptionTagTone>;

// Not strict: a field a later release adds is dropped by an older build
// rather than rejecting the whole option.
export const optionTagSchema = z.object({
  label: z.string().min(1),
  tone: OptionTagTone,
  /** Decorative icon before the label. */
  icon: IconNameEnum.optional(),
});
export type OptionTag = z.infer<typeof optionTagSchema>;

/**
 * Schema for a single option in the selection list.
 *
 * `feedback` requires a caller-supplied `feedbackLabel` (the design system
 * ships no answer copy). The refinement is runtime-only: the catalog's JSON
 * Schema export cannot express it, so both fields read as independently
 * optional there.
 */
export const optionSchema = z
  .object({
    label: z.string(),
    value: z.string(),
    description: z.string().optional(),
    /** Shows the option but stops it from being chosen; absent means enabled. */
    disabled: z.boolean().optional(),
    /**
     * How the description shows. `inline` (the default when absent) always shows
     * it under the label; `info-dot` hides it behind an (i) toggle after the
     * label that expands the card in place. Web only: native always shows it
     * inline. Needs both `description` and `infoDotAccessibilityLabel`, or the
     * description falls back to inline.
     */
    descriptionReveal: z.enum(['inline', 'info-dot']).optional(),
    /** Accessible name of the (i) toggle, e.g. "More about {label}". */
    infoDotAccessibilityLabel: z.string().optional(),
    /**
     * Reveals this option's answer state. Any option carrying it puts the
     * whole group in read-only reveal mode.
     */
    feedback: OptionFeedback.optional(),
    /** Visible text naming the revealed state, e.g. "Correct". */
    feedbackLabel: z.string().min(1).optional(),
    /**
     * The option's complete accessible name, already localized by the caller.
     * When absent, a revealed option is named "<label>, <feedbackLabel>"; pass
     * this wherever that order or punctuation is wrong for the locale.
     */
    accessibilityLabel: z.string().min(1).optional(),
  })
  .refine((option) => option.feedback === undefined || option.feedbackLabel !== undefined, {
    message: 'feedbackLabel is required when feedback is set',
    path: ['feedbackLabel'],
  });

export type SelectOption = z.infer<typeof optionSchema>;

/**
 * A SingleSelect option: the shared option plus a `tag` pill above its label.
 * Web only: native ignores the tag. MultiSelect options do not take one.
 */
// `extend` keeps the shared option's feedback-label refinement.
export const singleSelectOptionSchema = optionSchema.extend({ tag: optionTagSchema.optional() });

export type SingleSelectOption = z.infer<typeof singleSelectOptionSchema>;

/**
 * SingleSelect prop schema for the catalog (ADR-011).
 *
 * `onSelect` is a runtime callback defined on platform-specific interfaces
 * (`NativeSingleSelectProps`, `WebSingleSelectProps`), not in this catalog
 * schema. Catalog schemas define data/variant props only (ADR-011 convention).
 */
export const singleSelectPropsSchema = z.object({
  label: z.string(),
  /** Secondary text beneath the group label, supplied by the caller. */
  labelDescription: z.string().optional(),
  /**
   * Suppress the visible section label while keeping it as the group's
   * accessible name (the on-screen question header already labels the group).
   */
  hideLabel: z.boolean().default(false),
  /** Inline keeps the label and compact options on one row. */
  layout: z.enum(['stacked', 'inline']).default('stacked'),
  /** Replaces the visible label with feedback; the group's accessible name stays stable. */
  errorText: z.string().optional(),
  /** The selected option's value; `null` or absent means nothing is selected. */
  value: z.string().nullable().optional(),
  options: z.array(singleSelectOptionSchema),
  /**
   * Let the member clear their choice by activating the selected option again,
   * turning the list into a zero-or-one choice. Off by default: a plain
   * single-select keeps its selection when the selected option is re-pressed.
   */
  allowDeselect: z.boolean().default(false),
});

export type SingleSelectProps = z.infer<typeof singleSelectPropsSchema>;

/**
 * The value a press on `pressed` should commit. With `allowDeselect`, pressing
 * the option that is already selected clears the selection (`null`); every
 * other press selects the pressed option. Shared so native and web cannot drift.
 */
export function nextSingleSelectValue(
  current: string | null | undefined,
  pressed: string,
  allowDeselect: boolean,
): string | null {
  return allowDeselect && current === pressed ? null : pressed;
}

/**
 * Index of the option keyboard navigation should move to from `from`, stepping
 * by `step` and wrapping, skipping disabled options so arrow keys never land on
 * (or, in a select-on-move list, choose) one. Returns `from` when no other
 * option is selectable.
 */
export function nextEnabledOptionIndex(
  options: readonly SelectOption[],
  from: number,
  step: 1 | -1,
): number {
  const count = options.length;
  for (let offset = 1; offset < count; offset += 1) {
    const index = (((from + step * offset) % count) + count) % count;
    if (!options[index]?.disabled) return index;
  }
  return from;
}

/** The first option that can be chosen, or 0 when every option is disabled. */
export function firstEnabledOptionIndex(options: readonly SelectOption[]): number {
  const index = options.findIndex((option) => !option.disabled);
  return index === -1 ? 0 : index;
}

/** Colour pair for an option card based on selection state. */
interface OptionCardColors {
  background: string;
  border: string;
  labelText: string;
  descriptionText: string;
}

/**
 * Colours for an option card.
 *
 * `highlightSelectedBorder` controls whether selection paints the primary
 * (black) border: single-choice cards highlight (default true); multi-select
 * checkboxes convey selection via the tick indicator only, so they pass false
 * and keep the neutral divider border in both states (no selection outline).
 */
export function optionCardColorsFor(
  selected: boolean,
  highlightSelectedBorder = true,
): OptionCardColors {
  return {
    background: colors.background.container,
    border: selected && highlightSelectedBorder ? colors.button.primary : colors.stroke.divider,
    labelText: colors.text.primary,
    descriptionText: colors.text.secondary,
  };
}

/** True when any option reveals its answer state: the group is read-only. */
export function isRevealMode(options: readonly SelectOption[]): boolean {
  return options.some((option) => option.feedback !== undefined);
}

/** How a revealed option is drawn: border, decorative icon and text tone. */
export interface OptionFeedbackPresentation {
  borderColor: string;
  borderStyle: 'solid' | 'dashed';
  iconName: 'tick' | 'close' | 'tick-with-outlined-container';
  toneColor: string;
}

/**
 * Presentation for a revealed option. Each state differs by icon and visible
 * label as well as colour, and `missed` also by its dashed border, so no state
 * relies on colour alone. Shared so web and native cannot drift.
 */
export function optionFeedbackPresentation(feedback: OptionFeedback): OptionFeedbackPresentation {
  switch (feedback) {
    case 'correct':
      return {
        borderColor: colors.stroke.success,
        borderStyle: 'solid',
        iconName: 'tick',
        toneColor: colors.text.success,
      };
    case 'incorrect':
      return {
        borderColor: colors.stroke.alert,
        borderStyle: 'solid',
        iconName: 'close',
        toneColor: colors.text.alert,
      };
    case 'missed':
      return {
        borderColor: colors.stroke.success,
        borderStyle: 'dashed',
        iconName: 'tick-with-outlined-container',
        toneColor: colors.text.success,
      };
  }
}

/** Size of the decorative icon beside a revealed option's feedback label. */
export const FEEDBACK_ICON_SIZE = sizing.icon.xs;

/** Gap between a revealed option's icon and its feedback label. */
export const FEEDBACK_ROW_GAP = spacing.xs;

/** Typography for a revealed option's feedback label. */
export const FEEDBACK_LABEL_TYPOGRAPHY = typography.b3Regular;

/**
 * Constant border width for option cards in every state. Keeping the width
 * fixed (only the colour changes on selection) means selecting a card never
 * reflows its content — the border is reserved up-front rather than growing
 * from 1px to 2px on select.
 */
export const OPTION_BORDER_WIDTH = states.borderWidth.selected;

/** Border radius for option cards. */
export const OPTION_CARD_RADIUS = radius.xl;

/** Internal padding for option cards. */
export const OPTION_CARD_PADDING = spacing.lg;

/** Gap between label text and description text within an option card. */
export const OPTION_TEXT_GAP = spacing.xs;

/** Gap between the question label and the first option card. */
export const LABEL_TO_OPTIONS_GAP = spacing.m;

/** Gap between option cards. */
export const OPTION_GAP = spacing.s;

/** Typography for the question/prompt label above the options. */
export const LABEL_TYPOGRAPHY = typography.b1Bold;

/** Typography for option card labels. */
export const OPTION_LABEL_TYPOGRAPHY = typography.b1Regular;

/** Typography for option card descriptions (Regular weight, not Medium). */
export const OPTION_DESCRIPTION_TYPOGRAPHY = typography.b3Regular;

/** Gap between option text and the selection indicator (checkbox/radio). */
export const OPTION_CONTENT_GAP = spacing.xxl;

/** Focus ring styling for web option cards (ADR-025 Rule 2). */
export const FOCUS_RING_WIDTH = focusRing.width;
export const FOCUS_RING_OFFSET = focusRing.offset;
export const FOCUS_RING_COLOR = focusRing.color;

/** Opacity for disabled option cards. */
export const DISABLED_OPACITY = states.opacity.disabled;

/** Compact row dimensions reuse the existing inline-control scale. */
export const INLINE_SELECT_PADDING = spacing.s;
export const INLINE_SELECT_ROW_PADDING = spacing.sm;
export const INLINE_SELECT_OPTION_WIDTH = sizing.touchTarget.minimum;
export const INLINE_SELECT_GAP = spacing.s;
export const INLINE_SELECT_MIN_HEIGHT = sizing.touchTarget.minimum - spacing.xs;
/** Web has no native hitSlop: the rendered button itself owns the full target. */
export const INLINE_SELECT_WEB_MIN_HEIGHT = sizing.touchTarget.minimum;
export const INLINE_SELECT_LABEL_TYPOGRAPHY = typography.b2Bold;

/** Filled chip-style track for compact mutually exclusive choices. */
export const INLINE_SELECT_TRACK_COLOR = colors.background.container;
export const INLINE_SELECT_TRACK_RADIUS = radius.rounded;
export const INLINE_SELECT_TRACK_PADDING = 0;
export const INLINE_SELECT_SELECTED_TYPOGRAPHY = typography.c1Medium;

/** Figma baseline choice rows (3558:102808, 3561:21791). */
export const INLINE_SELECT_DESCRIPTION_TYPOGRAPHY = typography.b3Regular;
export const INLINE_SELECT_DESCRIPTION_COLOR = colors.text.secondary;
export const INLINE_SELECT_DIVIDER_COLOR = colors.stroke.divider;
export const INLINE_SELECT_BORDER_COLOR = colors.stroke.tertiary;
export const INLINE_SELECT_SELECTED_FILL = colors.background.dark;
export const INLINE_SELECT_SELECTED_TEXT = colors.text.inverted;
export const INLINE_SELECT_HORIZONTAL_PADDING = spacing.lg;
export const INLINE_SELECT_DESCRIPTION_GAP = 0;
export const INLINE_SELECT_BORDER_WIDTH = sizing.stroke.hairline;

/** Expand the 44dp Figma pill vertically to Android's 48dp touch minimum. */
export const INLINE_SELECT_HIT_SLOP = (sizing.touchTarget.minimum - INLINE_SELECT_MIN_HEIGHT) / 2;
